import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Eye, Edit3, Save, Loader2, AlertTriangle, Plus, Trash2, GripVertical,
  Type, Mail, Phone, CheckSquare, List
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CollaborateFormPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [accessType, setAccessType] = useState('view');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedField, setSelectedField] = useState(null);

  const fetchForm = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/forms/collaborate/${token}`);
      
      if (response.ok) {
        const data = await response.json();
        setForm(data.form);
        setAccessType(data.access_type);
      } else if (response.status === 410) {
        setError('This collaboration link has expired');
      } else if (response.status === 404) {
        setError('Invalid collaboration link');
      } else {
        setError('Failed to load form');
      }
    } catch (err) {
      setError('Failed to load form');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchForm();
  }, [fetchForm]);

  const handleSave = async () => {
    if (accessType !== 'edit') {
      toast.error('You only have view access to this form');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/forms/collaborate/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title,
          description: form.description,
          fields: form.fields
        })
      });
      
      if (response.ok) {
        toast.success('Form saved successfully');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to save form');
      }
    } catch (err) {
      toast.error('Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (fieldId, updates) => {
    if (accessType !== 'edit') return;
    
    setForm(prev => ({
      ...prev,
      fields: prev.fields.map(f => 
        f.id === fieldId ? { ...f, ...updates } : f
      )
    }));
  };

  const deleteField = (fieldId) => {
    if (accessType !== 'edit') return;
    
    setForm(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.id !== fieldId)
    }));
    setSelectedField(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Access Error</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/')}>
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold text-slate-900">{form?.title || 'Untitled Form'}</h1>
            <Badge variant={accessType === 'edit' ? 'default' : 'secondary'}>
              {accessType === 'edit' ? (
                <><Edit3 className="w-3 h-3 mr-1" /> Edit Access</>
              ) : (
                <><Eye className="w-3 h-3 mr-1" /> View Only</>
              )}
            </Badge>
          </div>
          
          {accessType === 'edit' && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Form Title & Description */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            {accessType === 'edit' ? (
              <>
                <Input
                  value={form?.title || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  className="text-2xl font-bold border-0 px-0 focus-visible:ring-0 mb-2"
                  placeholder="Form Title"
                />
                <Textarea
                  value={form?.description || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  className="border-0 px-0 focus-visible:ring-0 resize-none text-slate-600"
                  placeholder="Add a description (optional)"
                  rows={2}
                />
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">{form?.title}</h2>
                {form?.description && (
                  <p className="text-slate-600">{form.description}</p>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Form Fields */}
        <div className="space-y-4">
          {form?.fields?.map((field, index) => (
            <Card 
              key={field.id}
              className={`cursor-pointer transition-shadow ${
                selectedField === field.id ? 'ring-2 ring-indigo-500' : ''
              }`}
              onClick={() => accessType === 'edit' && setSelectedField(field.id)}
            >
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  {accessType === 'edit' && (
                    <div className="text-slate-400 cursor-grab">
                      <GripVertical className="w-5 h-5" />
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Label className="text-slate-900 font-medium">
                        {field.label || `Field ${index + 1}`}
                      </Label>
                      {field.required && (
                        <span className="text-red-500 text-sm">*</span>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {field.type}
                      </Badge>
                    </div>
                    
                    {/* Field Preview */}
                    <div className="bg-slate-50 rounded-lg p-3">
                      {field.type === 'text' || field.type === 'email' || field.type === 'phone' || field.type === 'url' ? (
                        <Input placeholder={field.placeholder || 'Enter text...'} disabled />
                      ) : field.type === 'textarea' ? (
                        <Textarea placeholder={field.placeholder || 'Enter text...'} disabled rows={3} />
                      ) : field.type === 'select' || field.type === 'radio' ? (
                        <div className="space-y-2">
                          {(field.options || ['Option 1', 'Option 2']).map((opt, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <div className="w-4 h-4 rounded-full border border-slate-300" />
                              <span className="text-sm text-slate-600">{opt}</span>
                            </div>
                          ))}
                        </div>
                      ) : field.type === 'checkbox' ? (
                        <div className="flex items-center gap-2">
                          <CheckSquare className="w-5 h-5 text-slate-400" />
                          <span className="text-sm text-slate-600">{field.label}</span>
                        </div>
                      ) : (
                        <div className="text-sm text-slate-500">
                          {field.type} field preview
                        </div>
                      )}
                    </div>
                    
                    {/* Edit Field Properties */}
                    {accessType === 'edit' && selectedField === field.id && (
                      <div className="mt-4 pt-4 border-t space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-sm">Label</Label>
                            <Input
                              value={field.label || ''}
                              onChange={(e) => updateField(field.id, { label: e.target.value })}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-sm">Placeholder</Label>
                            <Input
                              value={field.placeholder || ''}
                              onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={field.required || false}
                              onCheckedChange={(checked) => updateField(field.id, { required: checked })}
                            />
                            <Label className="text-sm">Required</Label>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteField(field.id);
                            }}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {form?.fields?.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-slate-500">This form has no fields yet.</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Footer */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-4">
        <span className="text-sm">
          Collaborating via shared link
        </span>
        {accessType === 'view' && (
          <span className="text-amber-400 text-sm">View only - changes won't be saved</span>
        )}
      </div>
    </div>
  );
};

export default CollaborateFormPage;
